import React from "react";
import {
  PaperClipIcon,
  PlusCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { PersonConsentForm, PersonDocument } from "../api/360Files";
import { classNames } from "../utils";
import ConsentFormRow from "./ConsentFormRow";
import LoadingSpinner from "../shared-components/LoadingSpinner";

const NOT_PROVIDED = "Not Provided";

export interface ConsentFormSectionProps {
  consentDocs: PersonDocument[];
  consentForms: PersonConsentForm[];
  /** "summary" matches Person Info label/link style on 360 summary page; "sidebar" is default. */
  variant?: "sidebar" | "summary";
  handleDelete?: (doc: PersonDocument) => void;
  handleAddConsent?: () => void;
  isDeleting?: boolean;
  isLoadingDocs?: boolean;
  docsLoadError?: boolean;
  onRetryLoadDocs?: () => void;
}

const ConsentFormSection: React.FC<ConsentFormSectionProps> = ({
  consentDocs,
  consentForms,
  variant = "sidebar",
  handleDelete,
  handleAddConsent,
  isDeleting = false,
  isLoadingDocs = false,
  docsLoadError = false,
  onRetryLoadDocs,
}) => {
  const hasConsentItems = consentDocs.length > 0 || consentForms.length > 0;
  const isSummary = variant === "summary";
  const showConsentDocs = !isLoadingDocs && !docsLoadError;

  return (
    <div className={classNames("mb-3 w-full", !isSummary ? "text-sm" : "")}>
      <div className="flex items-center gap-1.5 mb-1">
        <span
          className={
            isSummary ? "text-gray-800 font-bold" : "text-sm text-gray-500"
          }
        >
          Consent Form
        </span>
        {!isSummary && handleAddConsent && !isLoadingDocs && (
          <button type="button" onClick={handleAddConsent}>
            <PlusCircleIcon className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>
      <div className="relative">
        {(isLoadingDocs || isDeleting) && (
          <div className="flex items-center py-1">
            <LoadingSpinner className="w-6 h-6" />
          </div>
        )}
        {docsLoadError && !isLoadingDocs && (
          <div
            className={
              isSummary
                ? "leading-5 text-gray-500 mt-0.5"
                : "text-sm text-gray-500"
            }
          >
            <span>Unable to load documents.</span>{" "}
            {onRetryLoadDocs && (
              <button
                type="button"
                className="text-red-600 hover:underline"
                onClick={onRetryLoadDocs}
              >
                Retry
              </button>
            )}
          </div>
        )}
        {!hasConsentItems &&
          !isDeleting &&
          !isLoadingDocs &&
          !docsLoadError && (
            <span
              className={
                isSummary
                  ? "block leading-5 text-gray-500 mt-0.5"
                  : "block text-sm leading-5 text-gray-500"
              }
            >
              {NOT_PROVIDED}
            </span>
          )}
        {showConsentDocs &&
          consentDocs.length > 0 &&
          consentDocs.map((doc) =>
            isSummary ? (
              <a
                key={doc.url}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 overflow-hidden text-ellipsis whitespace-nowrap relative hover:underline pl-5 block mt-0.5"
              >
                <span className="absolute top-1 left-0 text-black">
                  <PaperClipIcon className="w-4 h-4" />
                </span>
                {doc.filename}
              </a>
            ) : (
              <div
                key={doc.url}
                className="flex gap-2 items-center mt-0.5 min-w-0 cursor-pointer select-none"
                onClick={() => window.open(doc.url, "_blank")}
              >
                <PaperClipIcon className="w-4 h-4 flex-shrink-0 text-gray-500" />
                <span
                  className="min-w-0 flex-1 truncate text-sm font-semibold leading-5 text-gray-700"
                  title={doc.filename}
                >
                  {doc.filename}
                </span>
                {handleDelete && (
                  <button
                    type="button"
                    className="p-0.5 -m-0.5 flex-shrink-0 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                    disabled={isDeleting}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(doc);
                    }}
                    title="Remove consent form"
                    aria-label="Remove consent form"
                  >
                    <XMarkIcon className="w-4 h-4" aria-hidden="true" />
                  </button>
                )}
              </div>
            )
          )}
        {consentForms.length > 0 &&
          consentForms.map((form) => (
            <ConsentFormRow
              key={form.signature_request_id}
              form={form}
              variant={variant}
            />
          ))}
      </div>
    </div>
  );
};

export default ConsentFormSection;
